import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { DocumentsData } from '@/features/ui/DocumentsData';
import { buildDocumentPreviewRows } from '@/features/ui/DocumentsData/model/preview';
import { deleteAttachmentFiles } from '@/features/ui/DocumentsData/ui/utils';
import { HealthDataSchema } from '@/features/ui/Steps/HealthData/model/types';
import { LearningDocumentSchema } from '@/features/ui/Steps/LearningDocument/model/types';

import { ConditionView, FormSelect, Grid, Loader, StepControlPanel } from '@/shared/ui';

import { YesNoSelectOptions } from '@/components/Forms/constants';

import { useAsyncDataLoader } from '@/hooks/useAsyncDataLoader';

import { getERZDocumentsSelect } from '@/modules/erz/helpers';

import { getIndividualStatusSelect } from '@/api/apiService';
import { DocumentTypeCode, PurchaseItemIdMapping } from '@/api/types';

import { HEALTH_DOC_STEP_NUMBER, LEARNING_DOCUMENT_STEP_NUMBER, MILITARY_STATUSES } from '../../lib/constants';
import { ObjectOfPurchaseSchema, PersonStatusDocumentsSchema } from '../../model/types';
import { STEPS } from '../../WeaponPurchasePermit';

export const PersonStatus: React.FC<StepProps> = ({ currentRequestId, prev, next, onStepDraftSave }) => {
  const { control, watch, setValue, getValues } = useFormContext<
    PersonStatusDocumentsSchema & ObjectOfPurchaseSchema & HealthDataSchema & LearningDocumentSchema
  >();
  const [hasPersonStatusChanged, setHasPersonStatusChanged] = useState<boolean>(false);
  const [hasFirearmDocChanged, setHasFirearmDocChanged] = useState<boolean>(false);
  const documents = watch('personStatus.documents');
  const personStatus = watch('personStatus');
  const personStatusDocuments = watch('personStatusDocuments');
  const firearmRegistrationDoc = watch('firearmRegistrationDoc');
  const firearmRegistrationDocument = watch('firearmRegistrationDocument');
  const hasValidPermit = watch('hasValidPermit');
  const objectOfPurchase = watch('objectOfPurchase');

  const fetchPersonStatuses = useCallback(
    () =>
      getIndividualStatusSelect({
        objectOfPurchase: objectOfPurchase.id === PurchaseItemIdMapping.DEVICE ? 'device' : '',
      }),
    [],
  );

  const {
    data: personStatuses = [],
    isLoading: isPersonStatusesLoading,
    load: loadPersonStatuses,
  } = useAsyncDataLoader(fetchPersonStatuses, {
    defaultDataValue: [],
  });

  const {
    data: erzDocuments = [],
    isLoading: isDocumentsLoading,
    load: loadERZDocuments,
  } = useAsyncDataLoader(
    async () =>
      documents?.length
        ? await getERZDocumentsSelect({ allowedDocumentTypeCodes: documents.map((document) => document.code) })
        : [],
    {
      defaultDataValue: [],
    },
  );

  const {
    data: otherDocuments = [],
    isLoading: isOtherDocumentsLoading,
    load: loadOtherDocuments,
  } = useAsyncDataLoader(
    async () => await getERZDocumentsSelect({ allowedDocumentTypeCodes: [DocumentTypeCode.OTHER] }),
    {
      defaultDataValue: [],
    },
  );

  const safeRequestId = currentRequestId ?? '';

  useEffect(() => {
    loadPersonStatuses();
    loadOtherDocuments();

    [LEARNING_DOCUMENT_STEP_NUMBER, HEALTH_DOC_STEP_NUMBER].forEach((step) => (STEPS[step - 1].skipped = false));

    if (!firearmRegistrationDoc) {
      setValue('firearmRegistrationDoc', YesNoSelectOptions.NO);
    }
  }, []);

  useEffect(() => {
    documents && loadERZDocuments();
  }, [documents]);

  useEffect(() => {
    if (!hasPersonStatusChanged) return;

    if (personStatusDocuments?.length) {
      personStatusDocuments.forEach((documents) => {
        deleteAttachmentFiles(documents, currentRequestId ?? '');
      });
      setValue('personStatusDocuments', []);
    }
    if (firearmRegistrationDocument?.length) {
      deleteAttachmentFiles(firearmRegistrationDocument, currentRequestId ?? '');
      setValue('firearmRegistrationDocument', []);
    }
  }, [personStatus, hasPersonStatusChanged]);

  useEffect(() => {
    if (firearmRegistrationDocument?.length && hasFirearmDocChanged) {
      deleteAttachmentFiles(firearmRegistrationDocument, currentRequestId ?? '');
      setValue('firearmRegistrationDocument', []);
    }
  }, [firearmRegistrationDoc, hasFirearmDocChanged]);

  const handleNextStep = useCallback(() => {
    if (hasPersonStatusChanged || hasFirearmDocChanged) {
      const { learningDocument, healthDataDocument } = getValues();
      deleteAttachmentFiles(learningDocument ?? [], currentRequestId ?? '');
      deleteAttachmentFiles(healthDataDocument ?? [], currentRequestId ?? '');
      setValue('learningDocument', []);
      setValue('healthDataDocument', []);
    }

    if (
      [
        PurchaseItemIdMapping.COLD_WEAPON,
        PurchaseItemIdMapping.HUNTING_RIFLED_WEAPONS,
        PurchaseItemIdMapping.HUNTING_SMOOTHBORE_WEAPONS,
        PurchaseItemIdMapping.BLANK_WEAPON,
        PurchaseItemIdMapping.PNEUMATIC_WEAPONS,
        PurchaseItemIdMapping.DEVICE,
      ].includes((objectOfPurchase?.id as PurchaseItemIdMapping) ?? '')
    ) {
      if (
        (objectOfPurchase?.id === PurchaseItemIdMapping.DEVICE && hasValidPermit) ||
        Number(firearmRegistrationDoc?.id)
      ) {
        [LEARNING_DOCUMENT_STEP_NUMBER, HEALTH_DOC_STEP_NUMBER].forEach((step) => (STEPS[step - 1].skipped = true));
        next();
        return;
      }

      next();
      return;
    }

    [LEARNING_DOCUMENT_STEP_NUMBER, HEALTH_DOC_STEP_NUMBER].forEach((step) => (STEPS[step - 1].skipped = true));
    next();
  }, [objectOfPurchase, hasValidPermit, firearmRegistrationDoc]);

  return (
    <ConditionView if={!isPersonStatusesLoading && !isOtherDocumentsLoading} else={<Loader />}>
      <FormBlock title="Статус особи">
        <FormSelect
          required
          control={control}
          name="personStatus"
          label="Назва статусу особи"
          options={personStatuses ?? []}
          onChange={() => {
            setHasPersonStatusChanged(true);
            setValue('firearmRegistrationDoc', YesNoSelectOptions.NO);
          }}
        />
      </FormBlock>
      <ConditionView if={!isDocumentsLoading} else={<Loader />}>
        {documents && erzDocuments?.length
          ? erzDocuments?.map((document, index) => {
              const statusDocument = documents?.find((doc) => doc.code === document.code);
              return (
                <FormBlock key={document.id} title={statusDocument?.label ?? document.label ?? ''}>
                  <DocumentsData
                    withUploader
                    documentTypeSelectHidden
                    onSaveDraft={onStepDraftSave}
                    documentTypeLabel={statusDocument?.label ?? ''}
                    documentUploadLabel={statusDocument?.label ?? ''}
                    name={`personStatusDocuments.${index}`}
                    documentTypeEntities={[document]}
                    request_id={safeRequestId}
                    documentsRequired={statusDocument?.required ?? false}
                    maxDocumentsCount={1}
                  />
                </FormBlock>
              );
            })
          : null}
        {MILITARY_STATUSES.includes(personStatus?.id) ? (
          <FormBlock title="Документ про закріплення вогнепальної зброї">
            <FormSelect
              required
              control={control}
              name="firearmRegistrationDoc"
              label="Документ про закріплення вогнепальної зброї"
              options={Object.values(YesNoSelectOptions)}
              onChange={() => setHasFirearmDocChanged(true)}
            />
            {Number(firearmRegistrationDoc?.id) ? (
              <DocumentsData
                withUploader
                documentTypeSelectHidden
                documentsRequired
                onSaveDraft={onStepDraftSave}
                name="firearmRegistrationDocument"
                documentTypeEntities={otherDocuments ?? []}
                documentUploadLabel="Сканкопія"
                request_id={safeRequestId}
                maxDocumentsCount={1}
              />
            ) : null}
          </FormBlock>
        ) : null}
      </ConditionView>
      <StepControlPanel prev={prev} next={handleNextStep} />
    </ConditionView>
  );
};

export const PersonStatusPreview: React.FC = () => {
  const { getValues } = useFormContext<PersonStatusDocumentsSchema>();
  const { personStatusDocuments, personStatus, firearmRegistrationDoc, firearmRegistrationDocument } = getValues();
  let personStatusDocumentsRows: Array<[string, Nullable<string>]> = [];
  personStatusDocuments.forEach((personStatusDocument) => {
    const personStatusDoc = Array.isArray(personStatusDocument) ? personStatusDocument[0] : undefined;
    const personStatusRows = buildDocumentPreviewRows(personStatusDoc, personStatusDoc?.type?.label);
    personStatusDocumentsRows = [...personStatusDocumentsRows, ...personStatusRows];
  });
  const firearmRegistration = Array.isArray(firearmRegistrationDocument) ? firearmRegistrationDocument[0] : undefined;
  const firearmRegistrationRows = buildDocumentPreviewRows(firearmRegistration, 'Сканкопія');
  const baseRows = [
    ['Назва статусу особи', personStatus?.label],
    ...personStatusDocumentsRows,
    ['Чи є документ про закріплення вогнепальної зброї?', firearmRegistrationDoc?.label],
    ...firearmRegistrationRows,
  ];

  return personStatus?.id ? <Grid title="Статус особи" data={baseRows as any} /> : null;
};
